import { sanitize } from 'isomorphic-dompurify'
import { forwardRef } from 'react'

import { AchievementBadge } from '../AchievementBadge/AchievementBadge'

import { theme, Tooltip, Typography, Box, Skeleton } from '@percent/lemonade'
import { useQuery } from '@percent/workplace-giving/common/hooks'
import { getAccountAchievements } from '@percent/workplace-giving/api/account/achievements/achievements'
import classes from './GamificationBadges.module.scss'

export const GamificationBadges = forwardRef<HTMLDivElement>(function GamificationBadges(_, ref) {
  const { data, status } = useQuery(['getAccountAchievements'], getAccountAchievements)

  const isLoading = status === 'pending' || status === 'error' || !data

  return (
    <div
      ref={ref}
      className={classes.AchievementsBox}
      data-testid={isLoading ? 'gamification-badges-container-loader' : 'gamification-badges-container'}
    >
      {isLoading ? (
        <Skeleton variant="circular" height={theme.sizes[12]} width={theme.sizes[12]} />
      ) : (
        data.rulesAndAchievements.map(({ accountAchievement, achievement: { name, image }, rule }) => {
          const ruleConditionsPassed = accountAchievement || rule.conditions.pass

          return (
            <Tooltip
              key={`${name}-tooltip`}
              lightBg
              lineClamp={0}
              placement="bottom-start"
              content={
                <Box py={8} px={4}>
                  <Typography className={classes.AchievementName} variant="small">
                    {name}
                  </Typography>
                  <Typography className={classes.AchievementStepsDescription}>{rule.conditions.description}</Typography>
                  <ul className={classes.StepsList}>
                    {rule.conditions.conditions?.flatMap(({ factKind, operator, description, descriptionLines }) =>
                      (descriptionLines ?? [description]).map(
                        desc =>
                          desc && (
                            <li
                              className={classes.StepsListItem}
                              key={`${operator}-${factKind}-${desc}`}
                              // eslint-disable-next-line react/no-danger
                              dangerouslySetInnerHTML={{
                                __html: sanitize(desc, { ALLOWED_TAGS: ['p', 'br'] })
                              }}
                            />
                          )
                      )
                    )}
                  </ul>
                </Box>
              }
            >
              <div data-testid="achievement-tooltip-trigger">
                <AchievementBadge image={image} name={name} isBlocked={!ruleConditionsPassed} />
              </div>
            </Tooltip>
          )
        })
      )}
    </div>
  )
})
